import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAtom, useAtomValue } from "jotai";
import { AppAtoms } from "../../lib/store";
import { isProductionEnv } from "../../config/const";

/** 一覧の上に置くタブ群の種別 */
export type PageTabGroup = "forms" | "analytics" | "payments";

type TabDef = { label: string; path: string; visible: boolean };

/** 可視条件は AppShell の buildNavGroups と App.tsx の Guard に合わせる */
function buildTabs(group: PageTabGroup, auth: string, db: string): TabDef[] {
  const M = auth === "マスターアカウント";
  const S = auth === "スタッフアカウント";
  const P = auth === "パートナー";
  const dbT = db === "トスアップアカウント";
  const dbE = db === "営業アカウント";

  switch (group) {
    case "forms":
      return [
        { label: "ETフォーム", path: "/entry_form", visible: M || S },
        { label: "TUフォーム", path: "/toss_up_form", visible: M || S },
        { label: "外部ETフォーム", path: "/outside_entry_form", visible: M || S },
      ];
    case "analytics":
      return [
        { label: "ランキング", path: "/runking", visible: !dbE && !dbT },
        { label: "推進実績", path: "/product_partner_result", visible: M || S },
        { label: "AI分析", path: "/data-analysis", visible: !isProductionEnv },
      ];
    case "payments":
      return [
        { label: "明細管理", path: "/payment_admin", visible: M },
        { label: "明細確認", path: "/payment", visible: M || P },
      ];
  }
}

/** 一覧の上に表示するタブバー。各タブは独立ルートへ遷移する（ページを内包しない） */
export function PageTabs({ group }: { group: PageTabGroup }) {
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const [spPageTabsHeight, setSpPageTabsHeight] = useAtom(AppAtoms.spPageTabsHeight);
  const location = useLocation();
  const navigate = useNavigate();
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const tabs = buildTabs(
    group,
    loginUser?.authority ?? "",
    loginUser?.db_authority ?? "",
  ).filter((t) => t.visible);

  /* catalog #10 t-* modifier: group別アクティブタブ下線色・文字色 */
  const activeColor =
    group === "forms" ? "var(--cat-entry-text)"
    : group === "payments" ? "var(--cat-closing-text)"
    : "var(--color-ai)";
  const activeBorderColor =
    group === "forms" ? "var(--cat-entry)"
    : group === "payments" ? "var(--cat-closing)"
    : "var(--color-kohaku)";

  // cmd-po_1243(R40): SP限定でfixed化したタブ帯の実高を計測しatomへ（TableTopBarが自身のtopに加算）。
  // ★早期return(tabs.length===0)より上に置く=hook順序を固定。tabs無し/PC/アンマウントは0に戻す。
  useEffect(() => {
    if (!isSmartPhone) {
      setSpPageTabsHeight(0);
      return;
    }
    const el = tabsRef.current;
    if (!el) {
      setSpPageTabsHeight(0);
      return;
    }
    const measure = () => setSpPageTabsHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
      setSpPageTabsHeight(0);
    };
  }, [isSmartPhone, tabs.length, setSpPageTabsHeight]);

  if (tabs.length === 0) return null;

  return (
    <>
    <Box
      ref={tabsRef}
      data-testid={`page-tabs-${group}`}
      sx={{
        display: "flex",
        gap: "var(--sp-1)",
        borderBottom: "1px solid var(--color-border)",
        // cmd-po_1243: SPはヘッダー直下にfixed帯化（下マージン0=spacerが穴を埋める）。PCは従来どおり mb=var(--sp-4)
        mb: isSmartPhone ? 0 : "var(--sp-4)",
        overflowX: "auto",
        flexShrink: 0,
        position: isSmartPhone ? "fixed" : undefined,
        top: isSmartPhone ? "var(--header-h, 56px)" : undefined,
        left: isSmartPhone ? 0 : undefined,
        right: isSmartPhone ? 0 : undefined,
        zIndex: isSmartPhone ? 100 : undefined,
        bgcolor: isSmartPhone ? "background.paper" : undefined,
        paddingLeft: isSmartPhone ? "var(--sp-3)" : undefined,
        paddingRight: isSmartPhone ? "var(--sp-3)" : undefined,
      }}
    >
      {tabs.map((t) => {
        const active = location.pathname === t.path;
        return (
          <Box
            key={t.path}
            component="button"
            type="button"
            data-testid={`page-tab-${t.path.slice(1)}`}
            onClick={() => {
              if (!active) navigate(t.path);
            }}
            sx={{
              padding: "var(--sp-2) var(--sp-4)",
              fontSize: 13,
              fontWeight: 600,
              color: active ? activeColor : "var(--color-sumi-light)",
              background: "none",
              border: "none",
              borderBottom: "2px solid",
              borderBottomColor: active ? activeBorderColor : "transparent",
              whiteSpace: "nowrap",
              cursor: "pointer",
              fontFamily: "inherit",
              "&:focus-visible": {
                outline: "2px solid var(--color-ai)",
                outlineOffset: "-2px",
              },
            }}
          >
            {t.label}
          </Box>
        );
      })}
    </Box>
    {/* cmd-po_1243(R40): fixed化でフローから外れたタブ帯の実高ぶんの隙間（content-jump防止）。SP限定=PCはDOMごと非描画でdiff0 */}
    {isSmartPhone && (
      <Box
        aria-hidden
        data-testid="page-tabs-spacer"
        sx={{ height: spPageTabsHeight, flexShrink: 0 }}
      />
    )}
    </>
  );
}

export default PageTabs;
